import React, { FC } from "react";
import { PlayerVM } from "../types";
import { Shirt } from "components/common/Shirt";
import { makeMetricIcons } from "./MetricIcons";

interface KitInfo {
  id: number | string;
  si?: string; // palette string from feed, e.g. "#ff0000,#000000"
  colors?: string[]; // fallback palette list
}

const pickShirtProps = (
  team: "home" | "away",
  homeKit?: KitInfo,
  awayKit?: KitInfo,
  homeColor?: string,
  awayColor?: string
) => {
  const toKit = (k?: KitInfo) => {
    if (!k || k.id == null) return undefined;
    const si =
      (typeof k.si === "string" && k.si.trim() !== "" ? k.si : undefined) ??
      (Array.isArray(k.colors) && k.colors.length
        ? k.colors.join(",")
        : undefined);
    if (!si) return undefined;
    const idNum = Number(k.id);
    if (!Number.isFinite(idNum)) return undefined;
    return { id: idNum, si } as const;
  };

  if (team === "home") {
    const k = toKit(homeKit);
    if (k) return { kit: k } as const;
    return { color: homeColor } as const;
  }

  const k = toKit(awayKit);
  if (k) return { kit: k } as const;
  return { color: awayColor } as const;
};

const ListView: FC<{
  rowsHome: PlayerVM[];
  rowsAway: PlayerVM[];
  homeColor?: string;
  awayColor?: string;
  homeKit?: KitInfo;
  awayKit?: KitInfo;
}> = ({ rowsHome, rowsAway, homeColor, awayColor, homeKit, awayKit }) => {
  const iconSizeLg = "48px";
  const iconSizeSm = "24px";
  const iconsLg = makeMetricIcons({ size: iconSizeLg });
  const iconsSm = makeMetricIcons({ size: iconSizeSm });
  const gridTemplate = "minmax(120px,1fr) repeat(9, clamp(48px, 8vw, 72px))";

  return (
    <div className="w-full overflow-hidden rounded-[1vw]">
      <div className="w-full overflow-x-auto border border-border">
        <div className="min-w-[700px]">
          <div
            className="grid gap-x-0 px-[3vw] py-[1vw] pr-0 bg-darkestBlue border-b border-border"
            style={{
              gridTemplateColumns: gridTemplate,
            }}
          >
            <div className="flex text-left text-[3vw] items-center font-extrabold text-statisticTitle">
              Player
            </div>
            <div className="text-center text-[2vw] font-extrabold text-[inherit] self-center">
              <span className="hidden lg:inline-block">{iconsLg.goals}</span>
              <span className="inline-block lg:hidden">{iconsSm.goals}</span>
            </div>
            <div className="text-center text-[3vw] font-extrabold text-[inherit] self-center">
              <span className="hidden lg:inline-block">{iconsLg.assists}</span>
              <span className="inline-block lg:hidden">{iconsSm.assists}</span>
            </div>
            <div className="text-center text-[3vw] font-extrabold text-[inherit] self-center">
              <span className="hidden lg:inline-block">{iconsLg.fouls}</span>
              <span className="inline-block lg:hidden">{iconsSm.fouls}</span>
            </div>
            <div className="text-center text-[3vw] font-extrabold text-[inherit] self-center">
              <span className="hidden lg:inline-block">{iconsLg.xg}</span>
              <span className="inline-block lg:hidden">{iconsSm.xg}</span>
            </div>
            <div className="text-center text-[3vw] font-extrabold text-[inherit] self-center">
              <span className="hidden lg:inline-block">{iconsLg.crosses}</span>
              <span className="inline-block lg:hidden">{iconsSm.crosses}</span>
            </div>
            <div className="text-center text-[3vw] font-extrabold text-[inherit] self-center">
              <span className="hidden lg:inline-block">
                {iconsLg.keyPasses}
              </span>
              <span className="inline-block lg:hidden">
                {iconsSm.keyPasses}
              </span>
            </div>
            <div className="text-center text-[3vw] font-extrabold text-[inherit] self-center">
              <span className="hidden lg:inline-block">{iconsLg.passes}</span>
              <span className="inline-block lg:hidden">{iconsSm.passes}</span>
            </div>
            <div className="text-center text-[3vw] font-extrabold text-[inherit] self-center">
              <span className="hidden lg:inline-block">{iconsLg.shots}</span>
              <span className="inline-block lg:hidden">{iconsSm.shots}</span>
            </div>
            <div className="text-center text-[3vw] font-extrabold text-[inherit] self-center">
              <span className="hidden lg:inline-block">{iconsLg.tackles}</span>
              <span className="inline-block lg:hidden">{iconsSm.tackles}</span>
            </div>
          </div>

          {[
            ...rowsHome.map((p) => ({ ...p, __team: "home" })),
            ...rowsAway.map((p) => ({ ...p, __team: "away" })),
          ].map((p: any) => (
            <div
              key={p.id}
              className="grid min-w-[700px] gap-x-0 px-[3vw] py-[1vw] pr-0 text-[3vw] border-b border-border"
              style={{
                gridTemplateColumns: gridTemplate,
              }}
            >
              <div className="truncate text-left">
                <span className="inline-flex items-center gap-[1vw]">
                  <Shirt
                    width={"3vw"}
                    height={"3vw"}
                    {...pickShirtProps(
                      p.__team,
                      homeKit,
                      awayKit,
                      homeColor,
                      awayColor
                    )}
                  />
                  <span>{p.name}</span>
                </span>
              </div>
              <div className="text-center">{p.goals || "-"}</div>
              <div className="text-center">{p.assists || "-"}</div>
              <div className="text-center">{p.fouls || "-"}</div>
              <div className="text-center">{p.xg ? p.xg.toFixed(2) : "-"}</div>
              <div className="text-center">{p.crosses || "-"}</div>
              <div className="text-center">{p.keyPasses || "-"}</div>
              <div className="text-center">{p.passes || "-"}</div>
              <div className="text-center">{p.shots || "-"}</div>
              <div className="text-center">{p.tackles || "-"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ListView;
